import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  LockClosedIcon,
  EyeIcon,
  ServerIcon,
  GlobeAltIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = "January 21, 2026";
  const effectiveDate = "January 21, 2026";

  const sections = [
    { id: 'introduction', title: '1. Introduction' },
    { id: 'controller', title: '2. Data Controller' },
    { id: 'data-collected', title: '3. Data We Collect' },
    { id: 'legal-basis', title: '4. Legal Basis for Processing' },
    { id: 'purpose', title: '5. Purpose of Processing' },
    { id: 'blockchain', title: '6. Blockchain Data' },
    { id: 'sharing', title: '7. Data Sharing' },
    { id: 'international', title: '8. International Transfers' },
    { id: 'retention', title: '9. Data Retention' },
    { id: 'rights', title: '10. Your Rights (GDPR)' },
    { id: 'security', title: '11. Security Measures' },
    { id: 'cookies', title: '12. Cookies' },
    { id: 'children', title: '13. Children\'s Privacy' },
    { id: 'changes', title: '14. Policy Changes' },
    { id: 'contact', title: '15. Contact Us' },
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-dark-900 to-dark-950">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <ShieldCheckIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-400 mb-6">
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal data in compliance with GDPR and other applicable regulations.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4" />
                Last Updated: {lastUpdated}
              </span>
              <span className="flex items-center gap-2">
                <DocumentTextIcon className="w-4 h-4" />
                Effective: {effectiveDate}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-semibold text-white mb-4">Quick Navigation</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm text-gray-400 hover:text-primary-400 transition-colors py-1"
                >
                  {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GDPR Highlights */}
      <section className="py-12 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <LockClosedIcon className="w-8 h-8 text-green-400 mb-4" />
                <h3 className="text-white font-semibold mb-2">Data Protection</h3>
                <p className="text-sm text-gray-400">Industry-standard encryption and security measures</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <EyeIcon className="w-8 h-8 text-blue-400 mb-4" />
                <h3 className="text-white font-semibold mb-2">Transparency</h3>
                <p className="text-sm text-gray-400">Clear information about how we use your data</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <UserGroupIcon className="w-8 h-8 text-purple-400 mb-4" />
                <h3 className="text-white font-semibold mb-2">Your Control</h3>
                <p className="text-sm text-gray-400">Full control over your personal information</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <GlobeAltIcon className="w-8 h-8 text-primary-400 mb-4" />
                <h3 className="text-white font-semibold mb-2">GDPR Compliant</h3>
                <p className="text-sm text-gray-400">Full compliance with EU data protection laws</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-invert prose-lg max-w-none">

              {/* Section 1 */}
              <section id="introduction" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">1</span>
                  Introduction
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    Welcome to TEMPLUM, a decentralized auction platform operated by NARDIHA Holdings S.L. ("Company," "we," "us," or "our"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
                  </p>
                  <p className="text-gray-300 mb-4">
                    We are committed to protecting your privacy and ensuring the security of your personal data. This policy has been designed to comply with the General Data Protection Regulation (GDPR) (EU) 2016/679, the Spanish Organic Law 3/2018 on Personal Data Protection and Digital Rights Guarantee (LOPDGDD), and other applicable data protection laws.
                  </p>
                  <p className="text-gray-300">
                    By accessing or using TEMPLUM, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with our policies and practices, please do not use our platform.
                  </p>
                </div>
              </section>

              {/* Section 2 */}
              <section id="controller" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">2</span>
                  Data Controller
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    The data controller responsible for your personal data is:
                  </p>
                  <div className="bg-dark-900/50 rounded-lg p-4 mb-4">
                    <p className="text-white font-semibold">NARDIHA Holdings S.L.</p>
                    <p className="text-gray-400">CIF: [Company Tax ID]</p>
                    <p className="text-gray-400">Registered Address: [Address], Spain</p>
                    <p className="text-gray-400">Email: privacy@templum.io</p>
                    <p className="text-gray-400">Data Protection Officer: dpo@nardiha.com</p>
                  </div>
                  <p className="text-gray-300">
                    For any questions or concerns regarding data protection or this Privacy Policy, please contact our Data Protection Officer (DPO) at the email address provided above.
                  </p>
                </div>
              </section>

              {/* Section 3 */}
              <section id="data-collected" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">3</span>
                  Data We Collect
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">3.1 Information You Provide Directly</h3>
                    <ul className="list-disc list-inside text-gray-300 space-y-2">
                      <li><strong>Account Information:</strong> Email address, username, profile picture (optional)</li>
                      <li><strong>Identity Verification:</strong> Name, date of birth, government ID documents (for KYC/AML compliance)</li>
                      <li><strong>Communication Data:</strong> Messages, support tickets, feedback you send us</li>
                      <li><strong>Transaction Information:</strong> Bid history, auction participation, payment details</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">3.2 Information Collected Automatically</h3>
                    <ul className="list-disc list-inside text-gray-300 space-y-2">
                      <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
                      <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform, click patterns</li>
                      <li><strong>Location Data:</strong> Approximate location based on IP address (not precise geolocation)</li>
                      <li><strong>Cookies and Tracking:</strong> See our <Link to="/legal/cookies" className="text-primary-400 hover:underline">Cookie Policy</Link></li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">3.3 Blockchain Data</h3>
                    <ul className="list-disc list-inside text-gray-300 space-y-2">
                      <li><strong>Wallet Addresses:</strong> Public blockchain addresses you connect to our platform</li>
                      <li><strong>Transaction Hashes:</strong> Records of on-chain transactions</li>
                      <li><strong>Smart Contract Interactions:</strong> Records of your interactions with our smart contracts</li>
                    </ul>
                    <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-yellow-200 text-sm">
                        <strong>Important:</strong> Blockchain transactions are publicly visible and immutable. Once data is recorded on the blockchain, it cannot be modified or deleted. We cannot control or be responsible for data recorded on public blockchains.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 4 */}
              <section id="legal-basis" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">4</span>
                  Legal Basis for Processing
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    Under GDPR Article 6, we process your personal data based on the following legal grounds:
                  </p>
                  <div className="space-y-4">
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Contract Performance (Art. 6(1)(b))</h4>
                      <p className="text-gray-400 text-sm">Processing necessary for providing our services, executing auctions, processing transactions, and managing your account.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Legal Obligation (Art. 6(1)(c))</h4>
                      <p className="text-gray-400 text-sm">Processing required to comply with AML/KYC regulations, tax reporting requirements, and other legal obligations.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Legitimate Interests (Art. 6(1)(f))</h4>
                      <p className="text-gray-400 text-sm">Processing for fraud prevention, platform security, service improvement, and analytics, balanced against your rights and freedoms.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Consent (Art. 6(1)(a))</h4>
                      <p className="text-gray-400 text-sm">Processing based on your explicit consent for marketing communications, non-essential cookies, and optional features.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 5 */}
              <section id="purpose" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">5</span>
                  Purpose of Processing
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">We use your personal data for the following purposes:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Service Provision</h4>
                      <ul className="text-gray-400 text-sm space-y-1">
                        <li>• Creating and managing your account</li>
                        <li>• Facilitating auction participation</li>
                        <li>• Processing bids and transactions</li>
                        <li>• Providing customer support</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Legal Compliance</h4>
                      <ul className="text-gray-400 text-sm space-y-1">
                        <li>• KYC/AML verification</li>
                        <li>• Fraud prevention and detection</li>
                        <li>• Regulatory reporting</li>
                        <li>• Legal dispute resolution</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Platform Improvement</h4>
                      <ul className="text-gray-400 text-sm space-y-1">
                        <li>• Analyzing platform usage</li>
                        <li>• Improving user experience</li>
                        <li>• Developing new features</li>
                        <li>• Bug fixing and maintenance</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Communication</h4>
                      <ul className="text-gray-400 text-sm space-y-1">
                        <li>• Transaction notifications</li>
                        <li>• Service announcements</li>
                        <li>• Marketing (with consent)</li>
                        <li>• Security alerts</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 6 */}
              <section id="blockchain" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">6</span>
                  Blockchain Data
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-6">
                    <p className="text-blue-200 text-sm">
                      <strong>Special Notice:</strong> TEMPLUM operates on the Polygon blockchain. Due to the nature of blockchain technology, certain data characteristics apply that differ from traditional data processing.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-white font-semibold mb-2">Public Nature of Blockchain Data</h4>
                      <p className="text-gray-300 text-sm">
                        All blockchain transactions are publicly visible. Your wallet address, transaction history, and smart contract interactions on the Polygon network are publicly accessible and cannot be made private or deleted.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">Immutability</h4>
                      <p className="text-gray-300 text-sm">
                        Once data is recorded on the blockchain, it cannot be modified, corrected, or erased. The right to erasure under GDPR Article 17 cannot be exercised for on-chain data due to technical impossibility.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">Pseudonymity</h4>
                      <p className="text-gray-300 text-sm">
                        While wallet addresses are pseudonymous, they may be linked to your identity if you provide identifying information to us or third parties. We recommend using separate wallets for privacy-sensitive activities.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 7 */}
              <section id="sharing" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">7</span>
                  Data Sharing
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">We may share your personal data with:</p>
                  <div className="space-y-4">
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">NARDIHA Ecosystem</h4>
                      <p className="text-gray-400 text-sm">Other platforms within the NARDIHA Holdings ecosystem to provide integrated services and unified user experience, subject to strict data protection agreements.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Service Providers</h4>
                      <p className="text-gray-400 text-sm">Third-party vendors providing hosting, analytics, KYC verification, payment processing, and customer support services, bound by data processing agreements.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Legal Requirements</h4>
                      <p className="text-gray-400 text-sm">Government authorities, regulatory bodies, or law enforcement when required by law, court order, or to protect our legal rights.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Business Transfers</h4>
                      <p className="text-gray-400 text-sm">In connection with a merger, acquisition, or sale of assets, your data may be transferred to the acquiring entity.</p>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-200 text-sm">
                      <strong>We do NOT:</strong> Sell your personal data to third parties for marketing purposes, share data with unauthorized parties, or use your data for purposes incompatible with those described in this policy.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 8 */}
              <section id="international" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">8</span>
                  International Transfers
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    Your personal data may be transferred to and processed in countries outside the European Economic Area (EEA). When we transfer data internationally, we ensure appropriate safeguards:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2">
                    <li><strong>Adequacy Decisions:</strong> Transfers to countries deemed adequate by the European Commission</li>
                    <li><strong>Standard Contractual Clauses:</strong> EU-approved contractual protections with data recipients</li>
                    <li><strong>Binding Corporate Rules:</strong> For transfers within the NARDIHA corporate group</li>
                    <li><strong>Consent:</strong> Where you have explicitly consented to the transfer</li>
                  </ul>
                  <p className="text-gray-300 mt-4">
                    You may request information about specific safeguards used for international transfers by contacting our DPO.
                  </p>
                </div>
              </section>

              {/* Section 9 */}
              <section id="retention" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">9</span>
                  Data Retention
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    We retain your personal data only for as long as necessary for the purposes described in this policy:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-dark-600">
                          <th className="text-left py-3 px-4 text-white">Data Category</th>
                          <th className="text-left py-3 px-4 text-white">Retention Period</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-400">
                        <tr className="border-b border-dark-700">
                          <td className="py-3 px-4">Account Information</td>
                          <td className="py-3 px-4">Duration of account + 5 years</td>
                        </tr>
                        <tr className="border-b border-dark-700">
                          <td className="py-3 px-4">KYC/AML Documents</td>
                          <td className="py-3 px-4">10 years (legal requirement)</td>
                        </tr>
                        <tr className="border-b border-dark-700">
                          <td className="py-3 px-4">Transaction Records</td>
                          <td className="py-3 px-4">10 years (tax/legal compliance)</td>
                        </tr>
                        <tr className="border-b border-dark-700">
                          <td className="py-3 px-4">Support Communications</td>
                          <td className="py-3 px-4">3 years after resolution</td>
                        </tr>
                        <tr className="border-b border-dark-700">
                          <td className="py-3 px-4">Marketing Preferences</td>
                          <td className="py-3 px-4">Until consent withdrawn</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4">Blockchain Data</td>
                          <td className="py-3 px-4">Permanent (immutable)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              {/* Section 10 - Your Rights */}
              <section id="rights" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">10</span>
                  Your Rights (GDPR)
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-6">
                    Under GDPR, you have the following rights regarding your personal data:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Right of Access (Art. 15)</h4>
                      <p className="text-gray-400 text-sm">Request a copy of your personal data and information about how we process it.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Right to Rectification (Art. 16)</h4>
                      <p className="text-gray-400 text-sm">Request correction of inaccurate or incomplete personal data.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Right to Erasure (Art. 17)</h4>
                      <p className="text-gray-400 text-sm">Request deletion of your personal data (subject to legal retention requirements and blockchain limitations).</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Right to Restriction (Art. 18)</h4>
                      <p className="text-gray-400 text-sm">Request limitation of processing in certain circumstances.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Right to Portability (Art. 20)</h4>
                      <p className="text-gray-400 text-sm">Receive your data in a structured, machine-readable format.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Right to Object (Art. 21)</h4>
                      <p className="text-gray-400 text-sm">Object to processing based on legitimate interests or for direct marketing.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Automated Decision-Making (Art. 22)</h4>
                      <p className="text-gray-400 text-sm">Not be subject to decisions based solely on automated processing.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Right to Withdraw Consent</h4>
                      <p className="text-gray-400 text-sm">Withdraw consent at any time for consent-based processing.</p>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-primary-500/10 border border-primary-500/30 rounded-lg">
                    <p className="text-primary-200 text-sm">
                      <strong>How to Exercise Your Rights:</strong> Contact our Data Protection Officer at dpo@nardiha.com or use the privacy controls in your account settings. We will respond within 30 days (extendable by 60 days for complex requests).
                    </p>
                  </div>
                  <div className="mt-4 p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Right to Lodge a Complaint</h4>
                    <p className="text-gray-400 text-sm">
                      You have the right to lodge a complaint with the Spanish Data Protection Agency (AEPD) at <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">www.aepd.es</a> or your local supervisory authority if you believe your rights have been violated.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 11 */}
              <section id="security" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">11</span>
                  Security Measures
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    We implement comprehensive technical and organizational measures to protect your personal data:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <ServerIcon className="w-6 h-6 text-green-400 mb-2" />
                      <h4 className="text-white font-semibold mb-2">Technical Measures</h4>
                      <ul className="text-gray-400 text-sm space-y-1">
                        <li>• TLS 1.3 encryption in transit</li>
                        <li>• AES-256 encryption at rest</li>
                        <li>• Multi-factor authentication</li>
                        <li>• Regular security audits</li>
                        <li>• Intrusion detection systems</li>
                        <li>• DDoS protection</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <LockClosedIcon className="w-6 h-6 text-blue-400 mb-2" />
                      <h4 className="text-white font-semibold mb-2">Organizational Measures</h4>
                      <ul className="text-gray-400 text-sm space-y-1">
                        <li>• Access control policies</li>
                        <li>• Employee training programs</li>
                        <li>• Data processing agreements</li>
                        <li>• Incident response procedures</li>
                        <li>• Regular privacy assessments</li>
                        <li>• Vendor security reviews</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-200 text-sm">
                      <strong>Data Breach Notification:</strong> In case of a personal data breach posing high risk to your rights, we will notify you without undue delay and report to the supervisory authority within 72 hours as required by GDPR Article 33.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 12 */}
              <section id="cookies" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">12</span>
                  Cookies
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    We use cookies and similar tracking technologies to enhance your experience on our platform. For detailed information about the cookies we use and how to manage them, please refer to our <Link to="/legal/cookies" className="text-primary-400 hover:underline">Cookie Policy</Link>.
                  </p>
                  <p className="text-gray-300">
                    You can manage your cookie preferences at any time through our cookie consent banner or your browser settings.
                  </p>
                </div>
              </section>

              {/* Section 13 */}
              <section id="children" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">13</span>
                  Children's Privacy
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    TEMPLUM is not intended for individuals under 18 years of age. We do not knowingly collect personal data from children. If we become aware that we have collected personal data from a child without parental consent, we will take steps to delete that information immediately.
                  </p>
                  <p className="text-gray-300">
                    If you believe we have inadvertently collected information from a child, please contact us at privacy@templum.io.
                  </p>
                </div>
              </section>

              {/* Section 14 */}
              <section id="changes" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">14</span>
                  Policy Changes
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2">
                    <li>We will update the "Last Updated" date at the top of this policy</li>
                    <li>We will notify you via email or prominent notice on our platform</li>
                    <li>For significant changes affecting your rights, we may seek your consent</li>
                  </ul>
                  <p className="text-gray-300 mt-4">
                    We encourage you to review this Privacy Policy periodically to stay informed about how we protect your data.
                  </p>
                </div>
              </section>

              {/* Section 15 */}
              <section id="contact" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">15</span>
                  Contact Us
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-6">
                    For any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-3">Data Protection Officer</h4>
                      <p className="text-gray-400 text-sm">Email: dpo@nardiha.com</p>
                      <p className="text-gray-400 text-sm">For GDPR rights requests and data protection inquiries</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-3">Privacy Team</h4>
                      <p className="text-gray-400 text-sm">Email: privacy@templum.io</p>
                      <p className="text-gray-400 text-sm">For general privacy questions and concerns</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg md:col-span-2">
                      <h4 className="text-white font-semibold mb-3">Postal Address</h4>
                      <p className="text-gray-400 text-sm">
                        NARDIHA Holdings S.L.<br />
                        Attn: Data Protection Officer<br />
                        [Registered Address]<br />
                        Spain
                      </p>
                    </div>
                  </div>
                </div>
              </section>

            </div>

            {/* Related Documents */}
            <div className="mt-12 p-6 bg-dark-800/50 rounded-xl border border-dark-700">
              <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/legal/terms"
                  className="px-4 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/legal/cookies"
                  className="px-4 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Cookie Policy
                </Link>
              </div>
            </div>

            {/* Back to top */}
            <div className="mt-8 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-primary-400 hover:text-primary-300 text-sm"
              >
                ↑ Back to top
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
